import React, { ChangeEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { getAllPlantilles } from 'api/queries'
import { Button, Select, MenuItem, InputLabel, SelectChangeEvent, Icon } from '@mui/material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  Typography
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useQuery } from '@tanstack/react-query'
import { ExternalLinks, RouteNames } from 'models/routes'
import { InfoOutlined } from '@mui/icons-material'

interface ModalCrearCopiaProps {
  modalOpen: boolean
  setModalOpen: (open: boolean) => void
}

const ModalCrearCopia = ({ modalOpen, setModalOpen }: ModalCrearCopiaProps) => {
  const navigate = useNavigate()

  const [isAmbPlantilla, setIsAmbPlantilla] = useState('sensePlantilla')
  const [plantillaSelectValue, setPlantillaSelectValue] = useState('')
  const [hasData, setHasData] = useState(false)

  useEffect(() => {
    setHasData(false)
  }, [modalOpen])

  const { data: plantilles, isFetching } = useQuery({
    queryFn: getAllPlantilles,
    queryKey: ['plantilles', 'all'],
    enabled: isAmbPlantilla === 'ambPlantilla' && !hasData,
    onSuccess: (data) => {
      setHasData(!!data?.body)
    },
    retry: false,
    useErrorBoundary: true
  })

  const handleRadioSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'sensePlantilla') {
      setPlantillaSelectValue('')
    }
    setIsAmbPlantilla(event.target.value)
  }

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setPlantillaSelectValue(String(event.target.value))
  }

  const handleClose = () => {
    setModalOpen(false)
    setPlantillaSelectValue('')
    setIsAmbPlantilla('sensePlantilla')
  }

  const handleCreateCopiaButton = () => {
    const nextState = {
      plantillaId: isAmbPlantilla === 'ambPlantilla' ? plantillaSelectValue : ''
    }
    navigate(RouteNames.novaCopia, { replace: false, state: nextState })
  }

  return (
    <div>
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        style={{ display: 'flex', justifyContent: 'center' }}>
        <DialogTitle variant="h4" sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            Selecciona una opció
            <span
              style={{ paddingLeft: '.5rem', cursor: 'pointer' }}
              onClick={() => window.open(ExternalLinks.createCopiaHelp, '_blank')}>
              <Icon
                component={InfoOutlined}
                sx={(theme) => ({ '&:hover': { color: theme.palette.secondary.main } })}
              />
            </span>
          </span>
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            paddingTop: '0.5rem',
            flexDirection: 'column'
          }}>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <FormLabel component="legend" />
            <RadioGroup
              value={isAmbPlantilla}
              onChange={handleRadioSelect}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
              <div style={{ display: 'flex' }}>
                <FormControlLabel
                  key={0}
                  value={'sensePlantilla'}
                  control={<Radio />}
                  label={'Sense plantilla'}
                />
              </div>

              <FormControlLabel
                key={1}
                value={'ambPlantilla'}
                control={<Radio />}
                style={{ marginRight: 0 }}
                label={
                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'center'
                    }}>
                    <Typography>Amb plantilla </Typography>
                    <div style={{ width: '1rem' }}>
                      {isFetching && <CircularProgress size={20} />}
                    </div>
                    <div>
                      <FormControl
                        style={{
                          display: 'flex',
                          width: '350px'
                        }}>
                        <InputLabel
                          id="select-plantilla-label"
                          disabled={
                            isAmbPlantilla === 'sensePlantilla' ||
                            isFetching ||
                            plantilles?.body?.length === 0
                          }>
                          Selecciona una plantilla
                        </InputLabel>
                        <Select
                          label="Selecciona una plantilla"
                          value={Number(plantillaSelectValue)}
                          onChange={handleSelectChange}
                          defaultValue={0}
                          disabled={
                            isAmbPlantilla === 'sensePlantilla' ||
                            isFetching ||
                            plantilles?.body?.length === 0
                          }>
                          {plantilles?.body?.map((option) => (
                            <MenuItem key={option.id ?? ''} value={option.id ?? ''}>
                              {option.nom_plantilla}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                }
              />
            </RadioGroup>
          </FormControl>
          <div
            style={{
              display: 'flex',
              alignSelf: 'flex-end',
              marginTop: '30px',
              gap: '1rem'
            }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel·lar
            </Button>
            <Button
              variant="contained"
              disabled={isAmbPlantilla === 'ambPlantilla' && plantillaSelectValue === ''}
              onClick={handleCreateCopiaButton}>
              Generar còpia autèntica
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ModalCrearCopia
